import type { GridStackWidget } from 'gridstack';
import type { DashboardWidgetContent } from '../Components/Dashboard/dashboardTypes';
import {
  BOOLEAN_WIDGET_TYPE,
  CUSTOM_CALLABLE_WIDGET_TYPE,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
  MOTOR_MULTI_DEFAULT_METRIC,
  MOTOR_MULTI_WIDGET_TYPE,
  MOTOR_WIDGET_TYPE,
  OTHER_WIDGET_TYPE,
  isMotorMultiWidgetDataKey,
  motorStreamKeysForPortAndMetrics,
  parseMotorMultiWidgetDataKey,
  SENSOR_OBJECTS,
  SENSOR_WIDGET_TYPE,
  drivebaseWidgetStreamDataKey,
  streamMetricSlug,
} from '../Components/Graphs/sensor-utils';
import { motorStreamKeysForPorts, motorStreamKeysForProjectPortAndMetrics, type StreamProjectContext } from './electronicsDashboardUtils';
import { escapePythonStringLiteral } from './pythonActionFormatting';
import { formatGeneratedPythonCall } from './pythonFormattingUtils';
import {
  consumeThroughLineBreak,
  extractCallArgsText,
  findMatchingDelimiterIndex,
  getIndentAtCodeIndex,
  offsetAtLineColumn,
  parseDoubleQuotedStringLiterals,
  containsPythonLineCommentOutsideStrings,
  stripPythonLineCommentsOutsideStrings,
  skipWhitespaceInCode,
  splitCodeLines,
} from './pythonSourceScanUtils';
import { DATA_STREAMING_REGION_START, findRegionSpanLineIndices, isDataStreamingRegionStartLine, REGION_END } from './programRegionMarkers';
import { getInsertLineBeforeRobotStartInCode } from './robotStartCallUtils';
import { resolveCustomCallableDisplayName } from '../Components/Dashboard/Widgets/widgetTitle';
import { COUNTDOWN_TIMER_WIDGET_TYPE } from './countdownTimerCodeUtils';

/** Maximum number of unique items in `DataStreamStore.set_request_list([...])`. */
export const MAX_SENSOR_STREAMS = 20;

/** Default `DataStreamStore.set_streaming_interval(...)` value in milliseconds when dashboard widgets require streams. */
export const DEFAULT_STREAMING_INTERVAL_MS = 150;

/** Matches `DataStreamStore.set_streaming_interval(...)` and legacy standalone `set_streaming_interval(...)`. */
const SET_STREAMING_INTERVAL_LINE_RE = /^\s*(?:DataStreamStore\.)?set_streaming_interval\s*\(\s*(\d+)\s*\)\s*$/;

/** Widget types with fixed stream keys when `dataKey` is `all` (not derived from `SENSOR_OBJECTS`). */
const STREAM_KEYS_BY_WIDGET_TYPE: Record<string, string[]> = {
  orientation: ['Locator Yaw'],
  field: ['Position X', 'Position Y', 'Locator Yaw'],
};

/** Widget types that never need `DataStreamStore.set_request_list` updates. */
const WIDGET_TYPES_WITHOUT_DATA_STREAM = new Set(['console', 'timer', COUNTDOWN_TIMER_WIDGET_TYPE]);

const SET_REQUEST_LIST_MARKER = 'set_request_list';

type SetRequestListCallSpan = {
  start: number;
  end: number;
  listInnerStart: number;
  listInnerEnd: number;
  /** Index of `DataStreamStore` for deriving the statement's line indent. */
  calleeStart: number;
};

/** Sorted JSON signature for comparing stream-key sets regardless of order. */
export function stableKeysSignature(keys: string[]): string {
  return JSON.stringify([...keys].sort());
}

/** Layout signature for dashboard stream code sync (union of widget stream keys). */
export function getDashboardStreamSyncLayoutSignature(widgets: GridStackWidget[]): string {
  return stableKeysSignature(collectUnionSensorKeysFromLayout(widgets));
}

/**
 * Stream keys required for a dashboard widget.
 * When `dataKey` is `all`, returns all series keys for that graph type (or widget-specific defaults).
 */
export function getStreamKeysForWidget(widgetType: string, dataKey: string): string[] {
  if (WIDGET_TYPES_WITHOUT_DATA_STREAM.has(widgetType)) return [];

  if (
    widgetType === MOTOR_WIDGET_TYPE ||
    widgetType === MOTOR_MULTI_WIDGET_TYPE ||
    widgetType === SENSOR_WIDGET_TYPE ||
    widgetType === OTHER_WIDGET_TYPE ||
    widgetType === BOOLEAN_WIDGET_TYPE
  ) {
    if (widgetType === MOTOR_MULTI_WIDGET_TYPE && dataKey && isMotorMultiWidgetDataKey(dataKey)) {
      return [];
    }
    return dataKey && dataKey !== 'all' ? [dataKey] : [];
  }

  const specialKeys = STREAM_KEYS_BY_WIDGET_TYPE[widgetType];
  if (specialKeys && dataKey === 'all') return [...specialKeys];

  if (dataKey && dataKey !== 'all') return [drivebaseWidgetStreamDataKey(widgetType, dataKey)];

  const graph = SENSOR_OBJECTS[widgetType];
  if (graph?.series?.length) {
    return graph.series.map((series) => series.dataKey);
  }

  if (specialKeys) return [...specialKeys];
  return [];
}

export function createDashboardWidgetContent(opts: {
  name: string;
  dataKey: string;
  curve?: string;
  style?: string;
  props?: object;
  options?: DashboardWidgetContent['options'];
  sensorKeys?: string[];
  streamProjectContext?: StreamProjectContext;
}): string {
  const sensorKeys = opts.sensorKeys ?? getStreamKeysForWidget(opts.name, String(opts.dataKey ?? ''));
  const draft: DashboardWidgetContent = {
    name: opts.name,
    curve: opts.curve ?? 'linear',
    style: opts.style ?? 'line',
    dataKey: opts.dataKey,
    sensorKeys,
    props: opts.props ?? {},
    ...(opts.options ? { options: opts.options } : {}),
  };
  const content: DashboardWidgetContent = {
    ...draft,
    sensorKeys: resolveWidgetSensorKeys(draft, opts.streamProjectContext),
  };
  return JSON.stringify(content);
}

/**
 * Effective stream keys for a widget. For `motor_multi`, derives keys from `selectedMotorMetrics`
 * so live ingest and `set_request_list` stay aligned with the chart even if `sensorKeys` is stale.
 */
export function resolveWidgetSensorKeys(content: DashboardWidgetContent, streamProjectContext?: StreamProjectContext): string[] {
  const { name, dataKey, sensorKeys, options } = content;

  if (name === COUNTDOWN_TIMER_WIDGET_TYPE) {
    return [];
  }

  if (name === BOOLEAN_WIDGET_TYPE && dataKey && isMotorStreamSensorKey(dataKey)) {
    const ports = options?.selectedMotorPorts;
    if (ports?.length) {
      const keys = motorStreamKeysForPorts(dataKey, ports);
      if (keys.length) return keys;
    }
    return sensorKeys;
  }

  if (name !== MOTOR_MULTI_WIDGET_TYPE || !isMotorMultiWidgetDataKey(dataKey)) {
    if (name === 'gyro') {
      return sensorKeys.map((key) => drivebaseWidgetStreamDataKey(name, key));
    }
    return sensorKeys;
  }

  const parsed = parseMotorMultiWidgetDataKey(dataKey);
  const port = options?.selectedMotorPort ?? parsed?.port;
  const prefix = parsed?.prefix ?? 'motor';
  const metrics = options?.selectedMotorMetrics;

  if (port != null && metrics?.length) {
    if (streamProjectContext) {
      const kind = prefix === 'mechanism' ? 'mechanism' : 'motor';
      const resolved = motorStreamKeysForProjectPortAndMetrics(streamProjectContext.portRows, streamProjectContext.projectCode, port, metrics, kind);
      if (resolved.length === metrics.length) return resolved;
    }
    const fromPersisted = metrics
      .map((metric) => sensorKeys.find((key) => streamMetricSlug(key) === metric))
      .filter((key): key is string => Boolean(key));
    if (fromPersisted.length === metrics.length) return fromPersisted;
    return motorStreamKeysForPortAndMetrics(port, prefix, metrics);
  }
  if (sensorKeys.length) return sensorKeys;
  if (port != null) {
    return motorStreamKeysForPortAndMetrics(port, prefix, [MOTOR_MULTI_DEFAULT_METRIC]);
  }
  return [];
}

export function parseDashboardWidgetContent(content: string | undefined): DashboardWidgetContent | null {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content) as Partial<DashboardWidgetContent>;
    if (!parsed.name) return null;
    const dataKey = String(parsed.dataKey ?? '');
    const storedSensorKeys = Array.isArray(parsed.sensorKeys)
      ? parsed.sensorKeys.filter((key): key is string => typeof key === 'string' && key.length > 0)
      : getStreamKeysForWidget(parsed.name, dataKey);
    const draft: DashboardWidgetContent = {
      name: parsed.name,
      curve: parsed.curve ?? 'linear',
      style: parsed.style ?? 'line',
      dataKey,
      sensorKeys: storedSensorKeys,
      props: parsed.props ?? {},
      ...(parsed.options ? { options: parsed.options } : {}),
    };
    return {
      ...draft,
      sensorKeys: resolveWidgetSensorKeys(draft),
    };
  } catch {
    return null;
  }
}

/** Reads `sensorKeys` from widget content, with a fallback for layouts saved before `sensorKeys` existed. */
export function getSensorKeysFromWidgetContent(content: string | undefined): string[] {
  return parseDashboardWidgetContent(content)?.sensorKeys ?? [];
}

/** Union of all `sensorKeys` across widgets (deduped; order follows dashboard layout, first-seen wins). */
export function collectUnionSensorKeysFromLayout(widgets: GridStackWidget[]): string[] {
  let keys: string[] = [];
  for (const widget of widgets) {
    keys = mergeStreamKeys(keys, getSensorKeysFromWidgetContent(widget.content));
  }
  return keys;
}

/**
 * Merges project layout with GridStack meta widgets (meta wins per id).
 * Use in live mode so stream ingest matches toolbar/widget edits before Redux layout catches up.
 */
export function mergeDashboardWidgetLayouts(projectLayout: GridStackWidget[], metaLayout: GridStackWidget[]): GridStackWidget[] {
  if (!metaLayout.length) return projectLayout;
  const byId = new Map<string, GridStackWidget>();
  for (const widget of projectLayout) {
    if (widget.id) byId.set(widget.id, widget);
  }
  for (const widget of metaLayout) {
    if (widget.id) byId.set(widget.id, widget);
  }
  return Array.from(byId.values());
}

/** Motor/mechanism stream keys referenced by widgets on the dashboard layout. */
export function collectMotorStreamKeysFromLayout(widgets: GridStackWidget[]): string[] {
  return collectUnionSensorKeysFromLayout(widgets).filter(isMotorStreamSensorKey);
}

/** Electronic sensor stream keys (`beam.port*`, `distance.port*`, etc.) on the dashboard layout. */
export function collectElectronicSensorStreamKeysFromLayout(widgets: GridStackWidget[]): string[] {
  return collectUnionSensorKeysFromLayout(widgets).filter(isElectronicSensorStreamSensorKey);
}

/** Other electronics stream keys (`bit_adapter.port*`, etc.) on the dashboard layout. */
export function collectOtherElectronicsStreamKeysFromLayout(widgets: GridStackWidget[]): string[] {
  return collectUnionSensorKeysFromLayout(widgets).filter(isOtherElectronicsStreamSensorKey);
}

/** Stream keys ingested as single-series float graphs (custom callables). */
export function collectFloatStreamGraphKeysFromLayout(widgets: GridStackWidget[]): string[] {
  return collectCustomCallableStreamKeysFromLayout(widgets);
}

/** Stream keys for user-registered DataStreamStore callables on the dashboard layout. */
export function collectCustomCallableStreamKeysFromLayout(widgets: GridStackWidget[]): string[] {
  const keys: string[] = [];
  for (const widget of widgets) {
    const content = parseDashboardWidgetContent(widget.content);
    if (content?.name === CUSTOM_CALLABLE_WIDGET_TYPE) {
      for (const key of content.sensorKeys) {
        if (key && !keys.includes(key)) keys.push(key);
      }
    }
  }
  return keys;
}

/**
 * Custom callable display labels for CSV export and similar UI: stream parameters from the
 * active layout plus any additional registrations found in project code.
 */
export function buildCustomCallableDisplayNameByParameterFromLayout(
  widgets: GridStackWidget[],
  registeredParametersFromCode: ReadonlySet<string>
): ReadonlyMap<string, string> {
  const labels = new Map<string, string>();
  const seen = new Set<string>();

  for (const widget of widgets) {
    const content = parseDashboardWidgetContent(widget.content);
    if (content?.name !== CUSTOM_CALLABLE_WIDGET_TYPE) continue;
    const parameter = content.dataKey;
    if (!parameter || seen.has(parameter)) continue;
    seen.add(parameter);
    labels.set(parameter, resolveCustomCallableDisplayName(parameter));
  }

  for (const parameter of registeredParametersFromCode) {
    if (seen.has(parameter)) continue;
    labels.set(parameter, resolveCustomCallableDisplayName(parameter));
  }

  return labels;
}

/**
 * Check if a widget can be added without exceeding the stream item limit.
 * Uses the union of `sensorKeys` on existing widgets plus the new widget's keys.
 */
export function canAddWidget(currentWidgets: GridStackWidget[], keysToAdd: string[]): { canAdd: boolean } {
  if (keysToAdd.length === 0) return { canAdd: true };

  const projectedKeys = mergeStreamKeys(collectUnionSensorKeysFromLayout(currentWidgets), keysToAdd);

  if (projectedKeys.length > MAX_SENSOR_STREAMS) {
    return { canAdd: false };
  }

  return { canAdd: true };
}

/**
 * Whether program code matches the expected dashboard streaming setup (request list and interval line).
 * An existing `DataStreamStore.set_streaming_interval(...)` value is accepted as-is so users can tune it manually.
 */
export function isStreamingSetupInSync(code: string, keys: string[]): boolean {
  if (keys.length === 0) {
    return !hasSetRequestListCall(code) && !hasSetStreamingIntervalCall(code) && !hasSpheroUtilImport(code);
  }

  const codeKeys = parseDataStreamRequestList(code) ?? [];
  return stableKeysSignature(keys) === stableKeysSignature(codeKeys) && hasSetStreamingIntervalCall(code);
}

/**
 * Whether stream keys, interval, and `set_request_list` formatting match the dashboard layout.
 *
 * Key membership is compared set-wise via `isStreamingSetupInSync`. When the key set already
 * matches, this also requires Ruff-style formatting (`isSetRequestListFormattingNormalized`).
 *
 * `useSyncDataStreamKeysFromLayout` treats a matching key set with stale one-line formatting as
 * out of sync, so the first live sync for a layout signature may dispatch a formatting-only
 * program update even when stream keys did not change.
 */
export function isDashboardProgramCodeInSync(code: string, widgets: GridStackWidget[]): boolean {
  const keys = collectUnionSensorKeysFromLayout(widgets);
  if (!isStreamingSetupInSync(code, keys)) return false;
  if (keys.length === 0) return true;
  return isSetRequestListFormattingNormalized(code, keys);
}

function getParsedRequestListKeysMatchingLayout(code: string, layoutKeys: string[]): string[] | null {
  const codeKeys = parseDataStreamRequestList(code);
  if (!codeKeys || stableKeysSignature(codeKeys) !== stableKeysSignature(layoutKeys)) {
    return null;
  }
  return codeKeys;
}

/**
 * Rebuilds dashboard streaming setup in program code: `set_request_list` and
 * `DataStreamStore.set_streaming_interval`.
 */
export function rebuildDashboardProgramCodeFromLayout(code: string, widgets: GridStackWidget[], intervalMs = DEFAULT_STREAMING_INTERVAL_MS): string {
  return rebuildDataStreamRequestListInCode(code, widgets, intervalMs);
}

/**
 * Rebuilds dashboard streaming setup in program code: `set_request_list` and
 * `DataStreamStore.set_streaming_interval` (only inserted when missing). Removes both when no widgets require stream data.
 */
export function rebuildDataStreamRequestListInCode(code: string, widgets: GridStackWidget[], intervalMs = DEFAULT_STREAMING_INTERVAL_MS): string {
  const keys = collectUnionSensorKeysFromLayout(widgets);

  if (keys.length === 0) {
    return removeStreamingSetupFromCode(code);
  }

  const requestLine = formatDataStreamRequestListLine(keys);
  const intervalLine = formatSetStreamingIntervalLine(intervalMs);

  if (isStreamingSetupInSync(code, keys)) {
    if (isSetRequestListFormattingNormalized(code, keys)) {
      return code;
    }
    const codeKeys = getParsedRequestListKeysMatchingLayout(code, keys) ?? keys;
    return replaceSetRequestListCall(code, formatDataStreamRequestListLine(codeKeys));
  }

  let next = code;

  if (hasSetRequestListCall(next)) {
    next = replaceSetRequestListCall(next, requestLine);
  } else {
    next = insertStreamingBlockBeforeRobotStart(next, requestLine, intervalLine);
    return next;
  }

  return insertSetStreamingIntervalLineIfMissing(next, intervalLine);
}

/**
 * Locates `DataStreamStore.get_instance().set_request_list([...])`, including multiline list literals.
 * Uses the same multiline call-arg scanner as `driveModeAutonomousActionUtils.extractCallArgsText`.
 *
 * **Supported shape:** `DataStreamStore` and `get_instance` must appear on the same line before
 * `set_request_list`, with the opening `(` on that line. Splitting the call across lines is not matched.
 */
function findSetRequestListCallSpan(code: string): SetRequestListCallSpan | null {
  const lines = splitCodeLines(code);

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';
    const markerIdx = line.indexOf(SET_REQUEST_LIST_MARKER);
    if (markerIdx === -1) continue;

    const linePrefix = line.slice(0, markerIdx);
    if (!linePrefix.includes('DataStreamStore') || !linePrefix.includes('get_instance')) continue;

    const parenIdx = line.indexOf('(', markerIdx + SET_REQUEST_LIST_MARKER.length);
    if (parenIdx === -1) continue;

    const extracted = extractCallArgsText(lines, lineIndex, parenIdx, { ignoreLineComments: true });
    if (!extracted) continue;

    const openOffset = offsetAtLineColumn(lines, lineIndex, parenIdx);
    const argsStart = skipWhitespaceInCode(code, openOffset + 1);
    if (code[argsStart] !== '[') continue;

    const listCloseIdx = findMatchingDelimiterIndex(code, argsStart, '[', ']', { ignoreLineComments: true });
    if (listCloseIdx === -1) continue;

    const closeParenIdx = findMatchingDelimiterIndex(code, openOffset, '(', ')', { ignoreLineComments: true });
    if (closeParenIdx === -1) continue;

    const markerGlobal = offsetAtLineColumn(lines, lineIndex, markerIdx);
    const dataStoreIdx = code.lastIndexOf('DataStreamStore', markerGlobal);
    const stmtLineStart = dataStoreIdx >= 0 ? code.lastIndexOf('\n', dataStoreIdx) + 1 : offsetAtLineColumn(lines, lineIndex, 0);

    return {
      start: stmtLineStart,
      end: consumeThroughLineBreak(code, closeParenIdx + 1),
      listInnerStart: argsStart + 1,
      listInnerEnd: listCloseIdx,
      calleeStart: dataStoreIdx >= 0 ? dataStoreIdx : stmtLineStart,
    };
  }

  return null;
}

function hasSetRequestListCall(code: string): boolean {
  return findSetRequestListCallSpan(code) !== null;
}

function removeStreamingSetupFromCode(code: string): string {
  let next = removeSetStreamingIntervalCall(code);
  next = removeSetRequestListCall(next);
  next = removeDataStreamingRegionBlock(next);
  next = removeSpheroUtilImport(next);
  return next.replace(/\n{3,}/g, '\n\n');
}

function hasSpheroUtilImport(code: string): boolean {
  return splitCodeLines(code).some((line) => /^\s*from\s+sphero_util\s+import\s+\*/.test(line));
}

function removeSpheroUtilImport(code: string): string {
  const lines = splitCodeLines(code).filter((line) => !/^\s*from\s+sphero_util\s+import\s+\*/.test(line));
  const next = lines.join('\n');
  return code.endsWith('\n') && next.length > 0 ? `${next}\n` : next;
}

function hasSetStreamingIntervalCall(code: string): boolean {
  return parseStreamingIntervalMs(code) !== null;
}

/** Reads the interval argument from `DataStreamStore.set_streaming_interval(...)`, if present. */
export function parseStreamingIntervalMs(code: string): number | null {
  for (const line of splitCodeLines(code)) {
    const match = line.match(SET_STREAMING_INTERVAL_LINE_RE);
    if (match) return Number(match[1]);
  }
  return null;
}

function formatSetStreamingIntervalLine(intervalMs: number): string {
  return `DataStreamStore.set_streaming_interval(${intervalMs})`;
}

function findSetStreamingIntervalLineSpan(code: string): { start: number; end: number } | null {
  const lines = splitCodeLines(code);
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';
    if (!SET_STREAMING_INTERVAL_LINE_RE.test(line)) continue;
    const start = offsetAtLineColumn(lines, lineIndex, 0);
    return { start, end: consumeThroughLineBreak(code, start + line.length) };
  }
  return null;
}

function removeSetStreamingIntervalCall(code: string): string {
  const span = findSetStreamingIntervalLineSpan(code);
  if (!span) return code;
  return code.slice(0, span.start) + code.slice(span.end).replace(/\n{3,}/g, '\n\n');
}

/** Inserts `DataStreamStore.set_streaming_interval(...)` only when absent; never overwrites a user-edited value. */
function indentPythonStatement(indent: string, statement: string): string {
  return statement
    .split('\n')
    .map((line) => `${indent}${line}`)
    .join('\n');
}

function getSetRequestListStatementIndent(code: string, span: SetRequestListCallSpan): string {
  return getIndentAtCodeIndex(code, span.calleeStart);
}

function insertSetStreamingIntervalLineIfMissing(code: string, intervalLine: string): string {
  if (hasSetStreamingIntervalCall(code)) return code;

  const requestSpan = findSetRequestListCallSpan(code);
  if (!requestSpan) return code;

  const indent = getSetRequestListStatementIndent(code, requestSpan);
  const insertText = `${indent}${intervalLine.trimStart()}\n`;
  return code.slice(0, requestSpan.end) + insertText + code.slice(requestSpan.end);
}

function getIndentBeforeRobotStart(code: string): string {
  const startLine = getInsertLineBeforeRobotStartInCode(code);
  if (startLine === null) return '';
  const lines = splitCodeLines(code);
  const line = lines[startLine - 1] ?? '';
  const match = line.match(/^(\s*)/);
  return match?.[1] ?? '';
}

function findDataStreamingRegionSpan(code: string): { start: number; end: number; endLine: number } | null {
  const lines = splitCodeLines(code);
  const span = findRegionSpanLineIndices(lines, isDataStreamingRegionStartLine);
  if (!span) return null;

  const { startLine, endLine } = span;
  const start = offsetAtLineColumn(lines, startLine, 0);
  const endLineText = lines[endLine] ?? '';
  const end = consumeThroughLineBreak(code, offsetAtLineColumn(lines, endLine, endLineText.length));
  return { start, end, endLine };
}

function removeDataStreamingRegionBlock(code: string): string {
  const span = findDataStreamingRegionSpan(code);
  if (!span) return code;
  return code.slice(0, span.start) + code.slice(span.end).replace(/\n{3,}/g, '\n\n');
}

/**
 * Inserts request list + interval before `robot.start()` (or at file end).
 * Wraps in a BPS region only on first-time insert; never re-adds region markers if the user removed them.
 */
function insertStreamingBlockBeforeRobotStart(code: string, requestLine: string, intervalLine: string): string {
  const lines = splitCodeLines(code);
  const insertLine = getInsertLineBeforeRobotStartInCode(code);
  const insertAt = insertLine !== null ? insertLine - 1 : lines.length;
  const indent = insertLine !== null ? getIndentBeforeRobotStart(code) : '';
  const contentLines = [...indentPythonStatement(indent, requestLine.trimStart()).split('\n'), `${indent}${intervalLine.trimStart()}`];

  const region = findDataStreamingRegionSpan(code);
  if (region) {
    const nextLines = [...lines.slice(0, region.endLine), ...contentLines, ...lines.slice(region.endLine)];
    const next = nextLines.join('\n');
    return code.endsWith('\n') || code.length === 0 ? `${next}\n` : next;
  }

  const blockLines = [`${indent}${DATA_STREAMING_REGION_START}`, ...contentLines, `${indent}${REGION_END}`, ''];
  const nextLines = [...lines.slice(0, insertAt), ...blockLines, ...lines.slice(insertAt)];
  const next = nextLines.join('\n');
  return code.endsWith('\n') || code.length === 0 ? `${next}\n` : next;
}

function removeSetRequestListCall(code: string): string {
  const span = findSetRequestListCallSpan(code);
  if (!span) return code;
  return code.slice(0, span.start) + code.slice(span.end).replace(/\n{3,}/g, '\n\n');
}

function replaceSetRequestListCall(code: string, nextLine: string): string {
  // Key-change replacements use layout order and drop inline list comments.
  // Formatting-only replacements pass parsed code order via `formatDataStreamRequestListLine`.
  const span = findSetRequestListCallSpan(code);
  if (!span) return code;
  const indent = getSetRequestListStatementIndent(code, span);
  // span.end consumes the statement's trailing newline; restore it so the next line is not concatenated.
  return code.slice(0, span.start) + indentPythonStatement(indent, nextLine.trimStart()) + '\n' + code.slice(span.end);
}

/** Reads stream item names from an existing `set_request_list([...])` call, if present. */
export function parseDataStreamRequestList(code: string): string[] | null {
  const span = findSetRequestListCallSpan(code);
  if (!span) return null;
  const inner = code.slice(span.listInnerStart, span.listInnerEnd);
  return parseDoubleQuotedStringLiterals(stripPythonLineCommentsOutsideStrings(inner));
}

export type PythonStreamDefinition = { name: string; micropythonType: string };

/**
 * Reorders FW-reported stream definitions to match `set_request_list` wire order.
 * Micropython emits payload values in request-list order; parser indexes definitions
 * by position, so notification order must match code order (BPS-687).
 */
export function reorderPythonStreamDefinitionsToRequestList(
  definitions: ReadonlyArray<PythonStreamDefinition>,
  requestListOrder: readonly string[] | null | undefined
): PythonStreamDefinition[] {
  if (!definitions.length) return [];
  if (!requestListOrder?.length) {
    return definitions.map((def) => ({ name: def.name, micropythonType: def.micropythonType }));
  }

  const byName = new Map<string, PythonStreamDefinition>();
  for (const def of definitions) {
    const name = String(def?.name ?? '').trim();
    if (!name || byName.has(name)) continue;
    byName.set(name, { name, micropythonType: def.micropythonType });
  }

  const ordered: PythonStreamDefinition[] = [];
  const used = new Set<string>();

  for (const key of requestListOrder) {
    const trimmed = String(key ?? '').trim();
    if (!trimmed) continue;
    const def = byName.get(trimmed);
    if (!def) continue;
    ordered.push(def);
    used.add(trimmed);
  }

  for (const def of definitions) {
    const name = String(def?.name ?? '').trim();
    if (!name || used.has(name)) continue;
    ordered.push({ name, micropythonType: def.micropythonType });
    used.add(name);
  }

  return ordered;
}

function formatDataStreamRequestListLine(keys: string[]): string {
  const items = keys.map((key) => `"${escapePythonStringLiteral(key)}"`).join(', ');
  return formatGeneratedPythonCall(`DataStreamStore.get_instance().set_request_list([${items}])`);
}

function getSetRequestListAnnotationScanText(code: string, span: SetRequestListCallSpan): string {
  const statement = code.slice(span.start, span.end).replace(/\r?\n$/, '');
  const lineBreak = code.indexOf('\n', span.end);
  const lineEnd = lineBreak === -1 ? code.length : lineBreak;
  return statement + code.slice(span.end, lineEnd);
}

function isSetRequestListFormattingNormalized(code: string, keys: string[]): boolean {
  const span = findSetRequestListCallSpan(code);
  if (!span) return false;

  if (containsPythonLineCommentOutsideStrings(getSetRequestListAnnotationScanText(code, span))) {
    return true;
  }

  const codeKeys = getParsedRequestListKeysMatchingLayout(code, keys);
  if (!codeKeys) return false;

  const indent = getSetRequestListStatementIndent(code, span);
  const expected = indentPythonStatement(indent, formatDataStreamRequestListLine(codeKeys).trimStart());
  const statement = code.slice(span.start, span.end).replace(/\r?\n$/, '');
  return statement === expected;
}

export function mergeStreamKeys(existing: string[], keysToAdd: string[]): string[] {
  const merged = [...existing];
  for (const key of keysToAdd) {
    if (!merged.includes(key)) merged.push(key);
  }
  return merged;
}
